/* A single, local-font composition for preview, thumbnail and print export. */
(function (root) {
  'use strict';
  const CREAM = '#f4efe5';
  const INK = '#202a44';
  const LOCAL_FONTS = { Georgia: 'Georgia, "Times New Roman", serif', 'Times New Roman': '"Times New Roman", Georgia, serif', serif: 'Georgia, "Times New Roman", serif' };
  const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter('it', { granularity: 'grapheme' }) : null;
  const glyphs = text => segmenter ? Array.from(segmenter.segment(text), part => part.segment) : Array.from(text);
  function clean(value, limit) {
    const text = String(value == null ? '' : value).normalize('NFC').replace(/[\u0000-\u001f\u007f-\u009f]/g, ' ').replace(/\s+/gu, ' ').trim();
    return glyphs(text).slice(0, limit).join('');
  }
  function normalizeName(value) { return clean(clean(value, 24).toLocaleUpperCase('it'), 24); }
  function normalizeColor(value) {
    const color = String(value || '').trim();
    if (/^#[0-9a-f]{6}$/i.test(color)) return color.toLowerCase();
    if (/^#[0-9a-f]{3}$/i.test(color)) return '#' + Array.from(color.slice(1)).map(c => c + c).join('').toLowerCase();
    return INK;
  }
  function setFont(ctx, size, family) { ctx.font = '400 ' + size.toFixed(3) + 'px ' + family; }
  function measure(ctx, text, size) {
    const m = ctx.measureText(text);
    return { advance: m.width, left: Number.isFinite(m.actualBoundingBoxLeft) ? m.actualBoundingBoxLeft : 0,
      right: Number.isFinite(m.actualBoundingBoxRight) ? m.actualBoundingBoxRight : m.width,
      ascent: Number.isFinite(m.actualBoundingBoxAscent) ? m.actualBoundingBoxAscent : size * .8,
      descent: Number.isFinite(m.actualBoundingBoxDescent) ? m.actualBoundingBoxDescent : size * .2 };
  }
  function fit(ctx, text, family, initialSize, maxWidth, maxHeight, tracked) {
    let size = initialSize, plan;
    for (let attempt = 0; attempt < 12; attempt++) {
      setFont(ctx, size, family);
      const pieces = tracked ? glyphs(text) : [text], spacing = tracked ? size * .18 : 0;
      let cursor = 0, left = Infinity, right = -Infinity, ascent = 0, descent = 0;
      const positions = pieces.map(piece => {
        const m = measure(ctx, piece, size), x = cursor;
        left = Math.min(left, x - m.left); right = Math.max(right, x + m.right);
        ascent = Math.max(ascent, m.ascent); descent = Math.max(descent, m.descent);
        cursor += m.advance + spacing;
        return { text: piece, x };
      });
      plan = { positions, left, right, ascent, descent, size };
      const scale = Math.min(1, maxWidth / Math.max(1, right - left), maxHeight / Math.max(1, ascent + descent));
      if (scale >= .999) return plan;
      size *= scale * .995;
    }
    return plan;
  }
  function drawCentered(ctx, plan, center, top, family) {
    setFont(ctx, plan.size, family);
    const origin = center - (plan.left + plan.right) / 2;
    for (const item of plan.positions) ctx.fillText(item.text, origin + item.x, top + plan.ascent);
  }
  function visibleWidth(ctx, text, size) {
    const m = measure(ctx, text, size);
    return Math.max(m.advance, m.left + m.right);
  }
  function ellipsis(ctx, text, maxWidth, size) {
    const parts = glyphs(text);
    while (parts.length && visibleWidth(ctx, parts.join('') + '\u2026', size) > maxWidth) parts.pop();
    return parts.join('') + '\u2026';
  }
  function editorialLines(ctx, text, maxWidth, size) {
    const lines = []; let line = '';
    for (const word of text.split(' ')) {
      const next = line ? line + ' ' + word : word;
      if (visibleWidth(ctx, next, size) <= maxWidth) line = next;
      else { if (line) lines.push(line); line = word; }
    }
    if (line) lines.push(line);
    const visible = lines.slice(0, 2).map(value => visibleWidth(ctx, value, size) > maxWidth ? ellipsis(ctx, value, maxWidth, size) : value);
    if (lines.length > 2) visible[1] = ellipsis(ctx, visible[1], maxWidth, size);
    return visible;
  }
  function render(options) {
    const { image, width, height, color, name, coverLines, fontFamily } = options || {};
    const w = Math.round(Number(width)), h = Math.round(Number(height));
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1 || w > 10000 || h > 10000 || w * h > 64000000) throw new RangeError('Formato del ritratto non valido.');
    if (!image || image.complete === false) throw new TypeError('Attendi il caricamento dell\u2019immagine prima di creare il ritratto.');
    const iw = Number(image.naturalWidth || image.width), ih = Number(image.naturalHeight || image.height);
    if (!Number.isFinite(iw) || !Number.isFinite(ih) || iw <= 0 || ih <= 0) throw new TypeError('Il ritratto non contiene un\u2019immagine valida.');
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Anteprima del ritratto non disponibile.');
    ctx.fillStyle = CREAM; ctx.fillRect(0, 0, w, h);
    const scale = Math.min(w / iw, h / ih), dw = iw * scale, dh = ih * scale;
    const left = Math.max(0, (w - dw) / 2), top = Math.max(0, (h - dh) / 2);
    const right = Math.max(0, w - left - dw), bottom = Math.max(0, h - top - dh);
    // Continue each source edge into the unused space, preserving its gradient along the other axis.
    // The full source remains centered and uncropped; only its outermost one-pixel strips are extended.
    if (left > 0) ctx.drawImage(image, 0, 0, 1, ih, 0, top, left, dh);
    if (right > 0) ctx.drawImage(image, Math.max(0, iw - 1), 0, 1, ih, left + dw, top, right, dh);
    if (top > 0) ctx.drawImage(image, 0, 0, iw, 1, left, 0, dw, top);
    if (bottom > 0) ctx.drawImage(image, 0, Math.max(0, ih - 1), iw, 1, left, top + dh, dw, bottom);
    if (left > 0 && top > 0) ctx.drawImage(image, 0, 0, 1, 1, 0, 0, left, top);
    if (right > 0 && top > 0) ctx.drawImage(image, Math.max(0, iw - 1), 0, 1, 1, left + dw, 0, right, top);
    if (left > 0 && bottom > 0) ctx.drawImage(image, 0, Math.max(0, ih - 1), 1, 1, 0, top + dh, left, bottom);
    if (right > 0 && bottom > 0) ctx.drawImage(image, Math.max(0, iw - 1), Math.max(0, ih - 1), 1, 1, left + dw, top + dh, right, bottom);
    ctx.drawImage(image, left, top, dw, dh);
    ctx.fillStyle = normalizeColor(color); ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    const family = Object.prototype.hasOwnProperty.call(LOCAL_FONTS, fontFamily) ? LOCAL_FONTS[fontFamily] : LOCAL_FONTS.Georgia;
    const marginX = w * .05, marginY = h * .05;
    const petName = normalizeName(name);
    if (petName) {
      const masthead = fit(ctx, petName, family, w * .29, w - marginX * 2, h * .15, false);
      drawCentered(ctx, masthead, w / 2, marginY, family);
    }
    const captions = Array.isArray(coverLines) ? coverLines.slice(0, 2).map(text => clean(text, 90)) : [];
    const size = Math.min(w * .027, h * .019), boxWidth = w * .35;
    setFont(ctx, size, family);
    captions.forEach((text, side) => {
      if (!text) return;
      const lines = editorialLines(ctx, text, boxWidth, size), lineHeight = size * 1.35;
      const descent = Math.max(...lines.map(line => measure(ctx, line, size).descent));
      lines.forEach((line, row) => {
        const m = measure(ctx, line, size), x = side ? w - marginX - m.right : marginX + m.left;
        const y = h - marginY - descent - (lines.length - 1 - row) * lineHeight;
        ctx.fillText(line, x, y);
      });
    });
    return canvas;
  }
  root.CCLDogueCover = { render, normalizeName, normalizeColor };
})(typeof window !== 'undefined' ? window : globalThis);
